/**
 * QA Panel — container abstraction.
 *
 * The panel UI is a single self-contained HTML document (see popupTemplate.ts)
 * that talks to the lobby over postMessage. It does NOT care whether it lives
 * in a separate browser window or an in-page iframe — message authentication is
 * by namespace, not origin, so `'*'` target works across either boundary.
 *
 * This seam isolates the two window-coupling concerns the bridge used to inline:
 *   - the postMessage / document.write target
 *   - the "is it still open / how do I close it" lifecycle
 *
 * Two implementations:
 *   - PopupContainer  — desktop, wraps window.open (extracted verbatim).
 *   - OverlayContainer — phone, a fixed full-viewport iframe on the lobby body.
 *
 * Desktop behavior must stay byte-for-byte identical; PopupContainer therefore
 * reproduces the exact open/close/heartbeat semantics the bridge had inline.
 */

/**
 * Detect a touch / coarse-pointer device. Desktop keeps the popup; phones get
 * the in-DOM overlay because navigating away (which a separate tab forces) ends
 * the QA tester's session.
 */
export function isCoarsePointer(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        if (typeof window.matchMedia === 'function') {
            return window.matchMedia('(pointer: coarse)').matches;
        }
    } catch {
        // matchMedia unavailable — fall through to the touch heuristic.
    }
    // Fallback: treat any device exposing touch points as coarse.
    return (
        'ontouchstart' in window ||
        (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
    );
}

/**
 * Common surface the bridge drives. Both implementations expose the same shape
 * so the postMessage marshalling never branches on container type.
 */
export interface PanelContainer {
    /** Mount the panel and write `html` into it. Returns false if mounting failed. */
    open(html: string): boolean;
    /** The Window to postMessage into (panel side). Null when not open. */
    target(): Window | null;
    /** Whether the panel is currently mounted/open. */
    isOpen(): boolean;
    /** Tear the panel down. */
    close(): void;
    /** Bring the panel to the user's attention (focus the window / no-op for overlay). */
    focus(): void;
    /**
     * Optional liveness poll. The popup can be closed by the OS window chrome,
     * so the bridge polls `popup.closed`; the overlay can only be closed
     * programmatically, so it returns null (no polling needed).
     */
    onExternalClose?(callback: () => void): () => void;
}
